import { map } from 'fp-ts/lib/Array'
import { pipe } from 'fp-ts/lib/function'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { match } from 'ts-pattern'

type ParamValue = string | number | boolean | null | undefined

function parseParamValue(value: string): ParamValue {
  return match(value)
    .with('true', () => true)
    .with('false', () => false)
    .with('null', () => null)
    .with('', () => undefined)
    .when(val => !isNaN(Number(val)), Number)
    .otherwise(() => value)
}

function getURLParamValues<T extends Record<string, ParamValue>>(
  params: URLSearchParams
) {
  const values: Partial<T> = {}
  return pipe(
    Array.from(params),
    map(([key, value]) => ({ [key]: parseParamValue(value) })),
    // TODO: ECP-14839 @typescript-eslint/consistent-type-assertions
    arr => arr.reduce((acc, curr) => ({ ...acc, ...curr }), values)
  ) as Partial<T>
}
/**
 * A hook that provides typesafe access to all URL search parameters
 * and allows setting multiple URL parameters.
 * Warning: This hook may not work as expected with param changes that are initiated with nav apis
 */
export const useURLSearchParams = <
  T extends Record<string, ParamValue>
>(): readonly [Partial<T>, (keyValuePairs: Partial<T>) => void] => {
  const [searchParams, setSearchParams] = useState<URLSearchParams>(() => {
    const currSearchParams = !globalThis.location
      ? new URLSearchParams()
      : new URLSearchParams(globalThis.location.search)
    return currSearchParams
  })

  const setQuery = useCallback((keyValuePairs: Partial<T>) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev)

      Object.entries(keyValuePairs).forEach(([key, value]) => {
        if (value === null || value === undefined) {
          newParams.delete(key)
        } else {
          newParams.set(key, String(value))
        }
      })
      const newUrl =
        globalThis.location.pathname +
        (newParams.toString() ? `?${newParams.toString()}` : '')
      globalThis.history.pushState({}, '', newUrl)
      return newParams
    })
  }, [])

  return useMemo(() => {
    const params = getURLParamValues<T>(searchParams)
    return [params, setQuery] as const
  }, [searchParams, setQuery])
}

/**
 * A hook that provides access to a single URL search parameter
 * and allows setting just that parameter.
 * Warning: This hook may not work as expected with param changes that are initiated with nav apis
 * @param key The URL parameter key to manage
 * @returns [value, setValue] tuple where value is the current param value and setValue updates it
 */
export function useURLSearchParam<V extends ParamValue>(
  key: string
): [V | undefined, (newValue: V | undefined) => void] {
  const [params, setQuery] = useURLSearchParams<Record<string, V>>()

  const value = params[key]
  const setValue = useCallback(
    (newValue: V | undefined) => {
      setQuery({ [key]: newValue })
    },
    [key, setQuery]
  )

  return [value, setValue] as const
}
